import { duration, easing } from "./transitions";

/** How route keys are derived for AnimatePresence — configure motion in PAGE_TRANSITION_MOTION only. */
export type PageTransitionScope = "section" | "full";

export type PageTransitionSectionRule = {
  prefix: string;
  /** Path segments to keep stable within this section (e.g. 2 → `/data-connectors/:id`). */
  stableSegmentCount?: number;
};

/**
 * Sections that share a parent layout shell. While navigating inside a section,
 * the app-level outlet should not remount (prevents KG/data-model flicker).
 */
export const PAGE_TRANSITION_SECTION_RULES: readonly PageTransitionSectionRule[] =
  [
    { prefix: "/knowledge-graph" },
    { prefix: "/data-connectors", stableSegmentCount: 2 },
    { prefix: "/explore" },
    { prefix: "/build" },
    { prefix: "/present" },
  ];

/** Single place to tune page enter/exit motion (durations come from design tokens). */
export const PAGE_TRANSITION_MOTION = {
  durationSeconds: parseMsToSeconds(duration.normal),
  ease: parseCubicBezier(easing.smooth),
  enterOffsetY: 8,
  exitOffsetY: -4,
} as const;

export const pageTransitionVariants = {
  initial: {
    opacity: 0,
    y: PAGE_TRANSITION_MOTION.enterOffsetY,
  },
  animate: {
    opacity: 1,
    y: 0,
  },
  exit: {
    opacity: 0,
    y: PAGE_TRANSITION_MOTION.exitOffsetY,
  },
} as const;

export function resolvePageTransitionKey(
  pathname: string,
  scope: PageTransitionScope = "full",
): string {
  if (scope === "full") {
    return pathname;
  }

  for (const rule of PAGE_TRANSITION_SECTION_RULES) {
    if (pathname === rule.prefix || pathname.startsWith(`${rule.prefix}/`)) {
      if (rule.stableSegmentCount) {
        const segments = pathname.split("/").filter(Boolean);
        const count = Math.min(segments.length, rule.stableSegmentCount);
        if (count > 0) {
          return `/${segments.slice(0, count).join("/")}`;
        }
      }
      return rule.prefix;
    }
  }

  const segments = pathname.split("/").filter(Boolean);
  return segments.length > 0 ? `/${segments[0]}` : "/";
}

function parseMsToSeconds(value: string): number {
  const ms = Number.parseInt(value.replace("ms", ""), 10);
  return Number.isFinite(ms) ? ms / 1000 : 0.25;
}

function parseCubicBezier(
  value: string,
): [number, number, number, number] {
  const match = value.match(/cubic-bezier\(([^)]+)\)/);
  if (!match) {
    return [0.4, 0, 0.2, 1];
  }

  const parts = match[1].split(",").map((part) => Number.parseFloat(part.trim()));
  if (parts.length === 4 && parts.every((part) => Number.isFinite(part))) {
    return [parts[0], parts[1], parts[2], parts[3]];
  }

  return [0.4, 0, 0.2, 1];
}
